import { AxiosError } from 'axios';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { getFriendlyError } from '../../utils/error';
import { LDFlags } from '../../config';
import featureConfig from '../../config/featureConfig';

import { getBuildingAttributeHash } from './getBuildingHash';
import { withDemoSundialSite } from './mocks/demoData';
import { getProperty, patchProperty } from './properties';
import { Property, PropertyPatchRequest } from './types';

export interface UseProperties {
  isLoading: boolean;
  isFetched: boolean;
  data: Property | undefined;
  error?: string;
  refetch: () => void;
  buildingAttributeHash?: string;
  updateProperty: (
    data: PropertyPatchRequest,
    options?: {
      onSuccess?: (data: Property) => void;
      onError?: (error: AxiosError) => void;
    },
  ) => void;
  isUpdatePropertyLoading: boolean;
  isUpdatePropertySuccess: boolean;
}

export const QUERY_KEY_PROPERTIES = 'properties';

const CACHE_TIME = 12 * 60 * 60 * 1000; // 12 hours

interface UsePropertiesOptions {
  fetchEnabled?: boolean;
  refetchInterval?: number;
}

export const useProperties = (propertyId?: string, options?: UsePropertiesOptions): UseProperties => {
  const { fetchEnabled, refetchInterval } = options ?? {};

  const {
    data,
    error: getPropertiesError,
    isLoading,
    isFetched,
    refetch,
  } = useQuery<Property | undefined, AxiosError>(
    [QUERY_KEY_PROPERTIES, propertyId],
    () => {
      if (propertyId) {
        return getProperty(propertyId);
      }
      return Promise.resolve(undefined);
    },
    {
      enabled: Boolean(fetchEnabled === undefined ? propertyId : propertyId && fetchEnabled),
      refetchInterval,
      staleTime: CACHE_TIME,
      cacheTime: CACHE_TIME,
    },
  );

  const flags = useFlags<LDFlags>();
  const isDemoMode = featureConfig.demoMode(undefined, flags);
  const property = useMemo(
    () => (isDemoMode && data ? withDemoSundialSite(data) : data),
    [isDemoMode, data],
  );

  const queryClient = useQueryClient();

  const {
    mutate: updatePropertyMutation,
    error: updatePropertyError,
    isLoading: isUpdatePropertyLoading,
    isSuccess: isUpdatePropertySuccess,
  } = useMutation<Property, AxiosError, PropertyPatchRequest>(
    (data: PropertyPatchRequest) => patchProperty(propertyId || '', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEY_PROPERTIES, propertyId]);
      },
    },
  );

  const updateProperty = (
    data: PropertyPatchRequest,
    options?: {
      onSuccess?: (data: Property) => void;
      onError?: (error: AxiosError) => void;
    },
  ) => {
    updatePropertyMutation(data, {
      onSuccess: (updatedProperty) => {
        options?.onSuccess?.(updatedProperty);
      },
      onError: (error) => {
        options?.onError?.(error);
      },
    });
  };

  const getPropertyError = () => {
    if (getPropertiesError) {
      return getFriendlyError(getPropertiesError, 'properties');
    }
    if (updatePropertyError) {
      return getFriendlyError(updatePropertyError, 'property');
    }
    return undefined;
  };

  return {
    data: property,
    refetch,
    error: getPropertyError(),
    isLoading,
    isFetched,
    buildingAttributeHash: property && propertyId ? getBuildingAttributeHash(property, propertyId) : undefined,
    updateProperty,
    isUpdatePropertyLoading,
    isUpdatePropertySuccess,
  };
};

export default useProperties;
